import {
  lazy,
  type ComponentType,
  type LazyExoticComponent,
  type ReactElement,
} from "react";
import LoginPage from "@/pages/login/LoginPage";
import SsoCallbackPage from "@/pages/login/SsoCallbackPage";
import ForceChangePasswordPage from "@/pages/login/ForceChangePasswordPage";
import ForgotPasswordPage from "@/pages/login/ForgotPasswordPage";
import PasswordResetConfirmPage from "@/pages/login/PasswordResetConfirmPage";
import AccountActivationPage from "@/pages/login/AccountActivationPage";
import NotFoundPage from "@/pages/errors/NotFoundPage";
import type { IconName } from "@/components/ui";
import type { RbacLevel, RbacModuleCode } from "@/config/rbac";
import { canShowMenu } from "@/config/access";

export type RouteHandle = {
  labelKey?: string;
  icon?: IconName;
  breadcrumb?: string;
  breadcrumbs?: BreadcrumbHandleItem[];
  module?: RbacModuleCode | string;
  moduleLevel?: RbacLevel;
  requiredPermission?: string | string[];
  requiredAllPermissions?: string[];
  hideInNav?: boolean;
  superadminOnly?: boolean;
};

export type BreadcrumbHandleItem = {
  label?: string;
  labelKey?: string;
  to?: string;
  icon?: IconName;
  usePageTitle?: boolean;
};

export type RouteSpec = {
  path: string;
  Component:
    | ComponentType<unknown>
    | LazyExoticComponent<ComponentType<unknown>>;
  handle?: RouteHandle;
  errorElement?: ReactElement;
  children?: RouteSpec[];
};

type PermissionAccess = {
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string[]) => boolean;
  hasModuleLevel: (moduleCode: string, level: RbacLevel) => boolean;
  isSuperadmin: boolean;
};

const AUTHENTICATED_PERMISSION = "isAuthenticated";
const DOCUMENT_READ_PERMISSIONS = [
  "document.view",
  "documents.view",
  "documents.list",
];

function hasRequiredPermission(
  required: string | string[] | undefined,
  access: PermissionAccess,
) {
  if (!required) return true;
  if (Array.isArray(required)) {
    if (required.includes(AUTHENTICATED_PERMISSION)) return true;
    return access.hasAnyPermission(required);
  }
  if (required === AUTHENTICATED_PERMISSION) return true;
  return access.hasPermission(required);
}

function hasAllRequiredPermissions(
  required: string[] | undefined,
  access: PermissionAccess,
) {
  if (!required?.length) return true;
  return required.every(
    (permission) =>
      permission === AUTHENTICATED_PERMISSION ||
      access.hasPermission(permission),
  );
}

function hasRequiredModuleLevel(
  handle: RouteHandle | undefined,
  access: PermissionAccess,
) {
  if (!handle?.module) return true;
  if (access.isSuperadmin) return true;
  return access.hasModuleLevel(handle.module, handle.moduleLevel ?? "VIEW");
}

export function canAccessRouteHandle(
  handle: RouteHandle | undefined,
  access: PermissionAccess,
) {
  if (handle?.superadminOnly && !access.isSuperadmin) return false;
  return (
    hasRequiredModuleLevel(handle, access) &&
    hasRequiredPermission(handle?.requiredPermission, access) &&
    hasAllRequiredPermissions(handle?.requiredAllPermissions, access)
  );
}

export function canAccessMenuRouteHandle(
  handle: RouteHandle | undefined,
  access: PermissionAccess,
) {
  if (handle?.superadminOnly && !access.isSuperadmin) return false;
  // RBAC/ACL v2: la visibilidad de un ítem de menú depende solo del nivel VIEW
  // del módulo del token (canShowMenu), sin roles hardcodeados. Así el Abogado
  // Director ve los módulos de su matriz sin tratamiento especial aquí.
  if (handle?.module) {
    if (access.isSuperadmin) return true;
    return canShowMenu(
      {
        hasModuleLevel: access.hasModuleLevel,
        hasPermission: access.hasPermission,
        isSuperadmin: access.isSuperadmin,
      },
      handle.module,
    );
  }
  return (
    hasRequiredPermission(handle?.requiredPermission, access) &&
    hasAllRequiredPermissions(handle?.requiredAllPermissions, access)
  );
}

const HOME_CURRENT: BreadcrumbHandleItem[] = [
  { labelKey: "nav.dashboard", icon: "home" },
];
const HOME_LINK: BreadcrumbHandleItem = {
  labelKey: "nav.dashboard",
  to: "/dashboard",
  icon: "home",
};
const WORK: BreadcrumbHandleItem = { labelKey: "nav.practice" };
const INTEL: BreadcrumbHandleItem = { labelKey: "nav.insights" };
const LIBRARY: BreadcrumbHandleItem = { labelKey: "nav.automation" };
const ADMIN: BreadcrumbHandleItem = { labelKey: "nav.admin" };

const crumbs = (...items: BreadcrumbHandleItem[]): BreadcrumbHandleItem[] => [
  HOME_LINK,
  ...items,
];

function routeHandle(
  module: RbacModuleCode,
  handle: Omit<RouteHandle, "module" | "moduleLevel">,
): RouteHandle {
  return { module, moduleLevel: "VIEW", ...handle };
}

const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const TasksPage = lazy(() => import("@/pages/tasks/TasksPage"));
const ProjectsPage = lazy(() => import("@/pages/projects/ProjectsPage"));
const ClientsPage = lazy(() => import("@/pages/clients/ClientsPage"));
const ProjectDetailPage = lazy(
  () => import("@/pages/projects/ProjectDetailPage"),
);
const ProjectAiChatPage = lazy(
  () => import("@/pages/projects/ProjectAiChatPage"),
);
const CounterpartiesPage = lazy(() => import("@/pages/CounterpartiesPage"));
const CounterpartyDetailPage = lazy(
  () => import("@/pages/CounterpartyDetailPage"),
);
const CounterpartyFormPage = lazy(() => import("@/pages/CounterpartyFormPage"));
const DocumentsPage = lazy(() => import("@/pages/documents/DocumentsPage"));
const DocumentEditorPage = lazy(
  () => import("@/pages/documents/DocumentEditorPage"),
);
const DocumentAiAnalysisPage = lazy(
  () => import("@/pages/documents/DocumentAiAnalysisPage"),
);
const DocumentJurisprudenciaPage = lazy(
  () => import("@/pages/documents/DocumentJurisprudenciaPage"),
);
const DocumentPlaybookComparePage = lazy(
  () => import("@/pages/documents/DocumentPlaybookComparePage"),
);
const DocumentSemanticDiffPage = lazy(
  () => import("@/pages/documents/DocumentSemanticDiffPage"),
);
const TemplatesPage = lazy(() => import("@/pages/templates/TemplatesPage"));
const LegacyTemplateAiComposeRedirect = lazy(
  () => import("@/pages/templates/LegacyTemplateAiComposeRedirect"),
);
const TemplateApprovalsPage = lazy(
  () => import("@/pages/templates/TemplateApprovalsPage"),
);
const TemplateFormPage = lazy(
  () => import("@/pages/templates/TemplateFormPage"),
);
const TemplateDetailPage = lazy(
  () => import("@/pages/templates/TemplateDetailPage"),
);
const TemplateVersionsPage = lazy(
  () => import("@/pages/templates/TemplateVersionsPage"),
);
const TemplateVersionEditorPage = lazy(
  () => import("@/pages/templates/TemplateVersionEditorPage"),
);
const TemplateClausesPage = lazy(
  () => import("@/pages/templates/TemplateClausesPage"),
);
const TemplateVariablesPage = lazy(
  () => import("@/pages/templates/TemplateVariablesPage"),
);
const TemplateRenderPage = lazy(
  () => import("@/pages/templates/TemplateRenderPage"),
);
const TemplateRenderLogPage = lazy(
  () => import("@/pages/templates/TemplateRenderLogPage"),
);
const WorkflowsPage = lazy(() => import("@/pages/workflows/WorkflowsPage"));
const WorkflowEditorPage = lazy(
  () => import("@/pages/workflows/WorkflowEditorPage"),
);
const WorkflowsInboxRedirect = lazy(
  () => import("@/pages/workflows/WorkflowsInboxRedirect"),
);
const WorkflowRunDetailPage = lazy(
  () => import("@/pages/workflows/WorkflowRunDetailPage"),
);
const AIAssistantPage = lazy(() => import("@/pages/ai/AIAssistantPage"));
const HelpPage = lazy(() => import("@/pages/help/HelpPage"));
const SettingsPage = lazy(() => import("@/pages/settings/SettingsPage"));
const AccountPage = lazy(() => import("@/pages/account/AccountPage"));
const RagPage = lazy(() => import("@/pages/rag/RagPage"));
const RisksPage = lazy(() => import("@/pages/risks/RisksPage"));
const ReportsPage = lazy(() => import("@/pages/reports/ReportsPage"));
const DashboardsHubPage = lazy(
  () => import("@/pages/reports/DashboardsHubPage"),
);
const ReportBuilderPage = lazy(
  () => import("@/pages/reports/ReportBuilderPage"),
);
const SignaturePage = lazy(() => import("@/pages/signature/SignaturePage"));
const AdminTenantListPage = lazy(
  () => import("@/pages/admin/tenants/AdminTenantListPage"),
);
const AdminTenantDetailPage = lazy(
  () => import("@/pages/admin/tenants/AdminTenantDetailPage"),
);
const RbacAdminPage = lazy(() => import("@/pages/admin/RbacAdminPage"));
const RiskCategoriesAdminPage = lazy(
  () => import("@/pages/admin/RiskCategoriesAdminPage"),
);
const MatterTypesPage = lazy(() => import("@/pages/admin/MatterTypesPage"));
const MatterTemplatesAdminPage = lazy(
  () => import("@/pages/admin/MatterTemplatesAdminPage"),
);
const ScoringPoliciesAdminPage = lazy(
  () => import("@/pages/admin/ScoringPoliciesAdminPage"),
);
const AuditAdminPage = lazy(() => import("@/pages/admin/AuditAdminPage"));
const ComplianceRequestsPage = lazy(
  () => import("@/pages/admin/ComplianceRequestsPage"),
);
const StabilityAdminPage = lazy(
  () => import("@/pages/admin/StabilityAdminPage"),
);
const SystemStatusPage = lazy(() => import("@/pages/admin/SystemStatusPage"));
const SystemConfigPage = lazy(() => import("@/pages/admin/SystemConfigPage"));
const AiConfigManagement = lazy(() =>
  import("@/pages/admin/AiConfigManagement").then((module) => ({
    default: module.default as ComponentType<unknown>,
  })),
);
const CostCenterPage = lazy(() => import("@/pages/admin/CostCenterPage"));
const ArchitecturePage = lazy(() => import("@/pages/admin/ArchitecturePage"));
const AdminNotificationsListPage = lazy(
  () => import("@/pages/notifications/AdminNotificationsListPage"),
);
const AdminEmailTemplatesPage = lazy(
  () => import("@/pages/notifications/AdminEmailTemplatesPage"),
);
const AdminEmailTemplateDetailPage = lazy(
  () => import("@/pages/notifications/AdminEmailTemplateDetailPage"),
);
const TenantNotificationsListPage = lazy(
  () => import("@/pages/notifications/TenantNotificationsListPage"),
);
const NotificationDetailPage = lazy(
  () => import("@/pages/notifications/NotificationDetailPage"),
);
const TenantNotificationSettingsPage = lazy(
  () => import("@/pages/notifications/TenantNotificationSettingsPage"),
);
const EmailConfigPage = lazy(() => import("@/pages/correos/EmailConfigPage"));
const EmailTemplatesPage = lazy(
  () => import("@/pages/correos/EmailTemplatesPage"),
);
const EmailTemplateEditorPage = lazy(
  () => import("@/pages/correos/EmailTemplateEditorPage"),
);
const LegacyEmailTemplatesRedirect = lazy(
  () => import("@/pages/correos/LegacyTemplatesRedirect"),
);
const LegacyEmailSettingRedirect = lazy(
  () => import("@/pages/correos/LegacyEmailSettingRedirect"),
);

const ShareGatePage = lazy(() => import("@/pages/share/ShareGatePage"));

export const PUBLIC_ROUTES: RouteSpec[] = [
  { path: "/login", Component: LoginPage },
  { path: "/forgot-password", Component: ForgotPasswordPage },
  {
    path: "/password-reset/confirm/:token",
    Component: PasswordResetConfirmPage,
  },
  { path: "/password-reset/confirm", Component: PasswordResetConfirmPage },
  // S51 (ADR-0036): activación de cuenta por enlace y afiliación a un tenant.
  { path: "/activar/:token", Component: AccountActivationPage },
  { path: "/activar", Component: AccountActivationPage },
  { path: "/force-change-password", Component: ForceChangePasswordPage },
  // S46 Wave B: vista pública de share links (sin shell ni guard de auth).
  { path: "/share/:token", Component: ShareGatePage },
  // S60: aterrizaje del callback SSO/AD (captura tokens del fragmento).
  { path: "/auth/sso/callback", Component: SsoCallbackPage },
];

export const PROTECTED_ROUTES: RouteSpec[] = [
  {
    path: "/dashboard",
    Component: DashboardPage,
    handle: routeHandle("dashboard", {
      labelKey: "nav.dashboard",
      icon: "home",
      breadcrumbs: HOME_CURRENT,
    }),
  },
  {
    path: "/tasks",
    Component: TasksPage,
    handle: routeHandle("tasks", {
      labelKey: "nav.tasks",
      icon: "inbox",
      requiredPermission: "worklist.item.view",
      breadcrumbs: crumbs(WORK, { labelKey: "nav.tasks" }),
    }),
  },
  {
    path: "/clients",
    Component: ClientsPage,
    handle: routeHandle("clients", {
      labelKey: "nav.clients",
      icon: "building",
      requiredPermission: "client.view",
      breadcrumbs: crumbs(WORK, { labelKey: "nav.clients" }),
    }),
  },
  {
    path: "/matters",
    Component: ProjectsPage,
    handle: routeHandle("matters", {
      labelKey: "nav.projects",
      icon: "briefcase",
      requiredPermission: ["matters.view", "projects.view"],
      breadcrumbs: crumbs(WORK, { labelKey: "nav.projects" }),
    }),
  },
  {
    path: "/matters/:projectId",
    Component: ProjectDetailPage,
    handle: routeHandle("matters", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.projects", to: "/matters" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/matters/:id/ai",
    Component: ProjectAiChatPage,
    handle: routeHandle("matters", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.projects", to: "/matters" },
        { label: "Chat IA del matter", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/matters/:projectId/documents/:docId/edit",
    Component: DocumentEditorPage,
    handle: routeHandle("documents", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.projects", to: "/matters" },
        { labelKey: "nav.documents" },
        { labelKey: "breadcrumb.edit", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/documents/:id/edit",
    Component: DocumentEditorPage,
    handle: routeHandle("documents", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.documents", to: "/documents" },
        { labelKey: "breadcrumb.edit", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/documents/:id/jurisprudencia",
    Component: DocumentJurisprudenciaPage,
    handle: routeHandle("documents", {
      hideInNav: true,
      requiredPermission: "document.jurisprudencia.search",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.documents", to: "/documents" },
        { label: "Jurisprudencia", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/documents/:id/playbook",
    Component: DocumentPlaybookComparePage,
    handle: routeHandle("risks", {
      hideInNav: true,
      requiredPermission: "risk.playbook.compare",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.documents", to: "/documents" },
        { label: "Comparar Playbook", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/documents/:id/diff",
    Component: DocumentSemanticDiffPage,
    handle: routeHandle("documents", {
      hideInNav: true,
      requiredPermission: "document.diff.read",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.documents", to: "/documents" },
        { label: "Diff semántico", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/counterparties",
    Component: CounterpartiesPage,
    handle: routeHandle("counterparties", {
      labelKey: "nav.counterparties",
      icon: "building",
      requiredPermission: "platform.counterparty.read",
      breadcrumbs: crumbs(WORK, { labelKey: "nav.counterparties" }),
    }),
  },
  {
    path: "/counterparties/new",
    Component: CounterpartyFormPage,
    handle: routeHandle("counterparties", {
      hideInNav: true,
      requiredPermission: "platform.counterparty.create",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.counterparties", to: "/counterparties" },
        { labelKey: "breadcrumb.new" },
      ),
    }),
  },
  {
    path: "/counterparties/:id",
    Component: CounterpartyDetailPage,
    handle: routeHandle("counterparties", {
      hideInNav: true,
      requiredPermission: "platform.counterparty.read",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.counterparties", to: "/counterparties" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/counterparties/:id/edit",
    Component: CounterpartyFormPage,
    handle: routeHandle("counterparties", {
      hideInNav: true,
      requiredPermission: "platform.counterparty.update",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.counterparties", to: "/counterparties" },
        { labelKey: "breadcrumb.edit", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/documents",
    Component: DocumentsPage,
    handle: routeHandle("documents", {
      labelKey: "nav.documents",
      icon: "docs",
      requiredPermission: DOCUMENT_READ_PERMISSIONS,
      breadcrumbs: crumbs(WORK, { labelKey: "nav.documents" }),
    }),
  },
  {
    path: "/documents/:id/ai-analysis",
    Component: DocumentAiAnalysisPage,
    handle: routeHandle("ai-assistant", {
      hideInNav: true,
      requiredPermission: "ai.document.analyze",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.documents", to: "/documents" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/matters/:projectId/documents/:docId/ai-analysis",
    Component: DocumentAiAnalysisPage,
    handle: routeHandle("ai-assistant", {
      hideInNav: true,
      requiredPermission: "ai.document.analyze",
      breadcrumbs: crumbs(
        WORK,
        { labelKey: "nav.projects", to: "/matters" },
        { labelKey: "nav.documents" },
        { label: "Análisis IA", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/ai",
    Component: AIAssistantPage,
    handle: routeHandle("ai-assistant", {
      labelKey: "nav.ai",
      icon: "spark",
      requiredPermission: "ai.chat.execute",
      breadcrumbs: crumbs(INTEL, { labelKey: "nav.ai" }),
    }),
  },
  {
    path: "/help",
    Component: HelpPage,
    handle: {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(INTEL, { label: "Ayuda" }),
    },
  },
  {
    path: "/rag",
    Component: RagPage,
    handle: routeHandle("knowledge-base", {
      labelKey: "nav.rag",
      icon: "database",
      requiredPermission: ["rag.source.view", "rag.search.execute"],
      breadcrumbs: crumbs(INTEL, { labelKey: "nav.rag" }),
    }),
  },
  {
    path: "/risks",
    Component: RisksPage,
    handle: routeHandle("risks", {
      labelKey: "nav.risks",
      icon: "alert",
      requiredPermission: "risk.view",
      breadcrumbs: crumbs(INTEL, { labelKey: "nav.risks" }),
    }),
  },
  {
    path: "/reports",
    Component: ReportsPage,
    handle: routeHandle("reports", {
      labelKey: "nav.reports",
      icon: "chart",
      requiredPermission: "reports.list",
      breadcrumbs: crumbs(INTEL, { labelKey: "nav.reports" }),
    }),
  },
  {
    path: "/dashboards",
    Component: DashboardsHubPage,
    handle: routeHandle("panels", {
      labelKey: "nav.dashboards",
      icon: "bars",
      requiredPermission: "report.dashboard.view",
      breadcrumbs: crumbs(INTEL, { labelKey: "nav.dashboards" }),
    }),
  },
  {
    path: "/reports/builder",
    Component: ReportBuilderPage,
    handle: routeHandle("reports", {
      hideInNav: true,
      requiredPermission: "report.definition.write",
      breadcrumbs: crumbs(
        INTEL,
        { labelKey: "nav.reports", to: "/reports" },
        { label: "Builder visual" },
      ),
    }),
  },
  {
    path: "/templates",
    Component: TemplatesPage,
    handle: routeHandle("templates", {
      labelKey: "nav.templates",
      icon: "library",
      requiredPermission: "template.view",
      breadcrumbs: crumbs(LIBRARY, { labelKey: "nav.templates" }),
    }),
  },
  {
    path: "/templates/new",
    Component: TemplateFormPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.create",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.newTemplate" },
      ),
    }),
  },
  {
    path: "/templates/approvals",
    Component: TemplateApprovalsPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.approval.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { label: "Aprobaciones" },
      ),
    }),
  },
  {
    path: "/templates/ai-compose",
    Component: LegacyTemplateAiComposeRedirect,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.view",
      breadcrumbs: crumbs(LIBRARY, {
        labelKey: "nav.templates",
        to: "/templates",
      }),
    }),
  },
  {
    path: "/templates/:id",
    Component: TemplateDetailPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/templates/:id/edit",
    Component: TemplateFormPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.update",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.detail", to: "/templates/:id" },
        { labelKey: "breadcrumb.editTemplate" },
      ),
    }),
  },
  {
    path: "/templates/:id/versions",
    Component: TemplateVersionsPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.version.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.versions", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/templates/:id/versions/new",
    Component: TemplateVersionEditorPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.version.create",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.versions" },
        { labelKey: "breadcrumb.newVersion" },
      ),
    }),
  },
  {
    path: "/templates/:id/versions/:versionId",
    Component: TemplateVersionEditorPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.version.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.versions" },
        { label: "Versión" },
      ),
    }),
  },
  {
    path: "/templates/:id/clauses",
    Component: TemplateClausesPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: ["template.clause.view", "template.view"],
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.clauses", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/templates/:id/variables",
    Component: TemplateVariablesPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: "template.variable.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.variables", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/templates/:id/render",
    Component: TemplateRenderPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      requiredPermission: ["template.render", "template.render.execute"],
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.render", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/admin/templates/render-log",
    Component: TemplateRenderLogPage,
    handle: routeHandle("templates", {
      hideInNav: true,
      superadminOnly: true,
      requiredPermission: "template.view",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.templates", to: "/templates" },
        { labelKey: "breadcrumb.renderLog" },
      ),
    }),
  },
  {
    path: "/workflows",
    Component: WorkflowsPage,
    handle: routeHandle("workflows", {
      labelKey: "nav.workflows",
      icon: "workflow",
      requiredPermission: "workflows.list",
      breadcrumbs: crumbs(LIBRARY, { labelKey: "nav.workflows" }),
    }),
  },
  {
    path: "/workflows/:id",
    Component: WorkflowEditorPage,
    handle: routeHandle("workflows", {
      hideInNav: true,
      requiredPermission: "workflow.definition.view",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.workflows", to: "/workflows" },
        { labelKey: "breadcrumb.workflowEditor", usePageTitle: true },
      ),
    }),
  },
  {
    // Redirect legado (S45): el inbox de workflows vive ahora en /tasks.
    path: "/workflows/inbox",
    Component: WorkflowsInboxRedirect,
    handle: {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
    },
  },
  {
    path: "/workflows/runs/:id",
    Component: WorkflowRunDetailPage,
    handle: routeHandle("workflows", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        LIBRARY,
        { labelKey: "nav.workflows", to: "/workflows" },
        { labelKey: "breadcrumb.workflowRun", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/signature",
    Component: SignaturePage,
    handle: routeHandle("signature", {
      labelKey: "nav.signature",
      icon: "signature",
      requiredPermission: "signature.read",
      breadcrumbs: crumbs(LIBRARY, { labelKey: "nav.signature" }),
    }),
  },
  {
    path: "/admin/tenants",
    Component: AdminTenantListPage,
    handle: routeHandle("organizations", {
      labelKey: "nav.admin.tenants",
      icon: "building",
      superadminOnly: true,
      requiredPermission: "tenant.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.admin.tenants" }),
    }),
  },
  {
    path: "/admin/tenants/:id",
    Component: AdminTenantDetailPage,
    handle: routeHandle("organizations", {
      hideInNav: true,
      superadminOnly: true,
      requiredPermission: "tenant.view",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.admin.tenants", to: "/admin/tenants" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/admin/rbac",
    Component: RbacAdminPage,
    handle: routeHandle("users-roles", {
      labelKey: "nav.rbac",
      icon: "users",
      requiredPermission: "roles.manage",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.rbac" }),
    }),
  },
  {
    path: "/admin/risk-categories",
    Component: RiskCategoriesAdminPage,
    handle: routeHandle("risk-categories", {
      labelKey: "nav.riskCategories",
      icon: "alert",
      requiredPermission: "risk.category.admin",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.riskCategories" }),
    }),
  },
  {
    path: "/admin/matter-types",
    Component: MatterTypesPage,
    handle: routeHandle("matter-types", {
      labelKey: "nav.matterTypes",
      icon: "tag",
      requiredPermission: "platform.matter-type.admin",
      breadcrumbs: crumbs(ADMIN, { label: "Matter types" }),
    }),
  },
  {
    path: "/admin/matter-templates",
    Component: MatterTemplatesAdminPage,
    handle: routeHandle("matter-templates", {
      labelKey: "nav.matterTemplates",
      icon: "template",
      requiredPermission: "matter.template.manage",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.matterTemplates" }),
    }),
  },
  {
    path: "/admin/scoring-policies",
    Component: ScoringPoliciesAdminPage,
    handle: routeHandle("risk-scoring", {
      labelKey: "nav.scoringPolicies",
      icon: "gauge",
      requiredPermission: "risk.scoring.admin",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.scoringPolicies" }),
    }),
  },
  {
    path: "/admin/audit",
    Component: AuditAdminPage,
    handle: routeHandle("audit", {
      labelKey: "nav.audit",
      icon: "scroll",
      superadminOnly: true,
      requiredPermission: "audit.ops.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.audit" }),
    }),
  },
  {
    path: "/admin/compliance-requests",
    Component: ComplianceRequestsPage,
    handle: {
      labelKey: "nav.complianceRequests",
      icon: "shieldAlt",
      requiredPermission: "compliance.request.read",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.complianceRequests" }),
    },
  },
  {
    path: "/admin/stability",
    Component: StabilityAdminPage,
    handle: routeHandle("system-pulse", {
      labelKey: "nav.stability",
      icon: "gauge",
      superadminOnly: true,
      requiredPermission: "audit.errors.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.stability" }),
    }),
  },
  {
    path: "/admin/system-status",
    Component: SystemStatusPage,
    handle: routeHandle("system-status", {
      labelKey: "nav.systemStatus",
      icon: "activity",
      superadminOnly: true,
      requiredPermission: "system.status.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.systemStatus" }),
    }),
  },
  {
    path: "/admin/system-config",
    Component: SystemConfigPage,
    handle: routeHandle("system-config", {
      labelKey: "nav.system",
      icon: "settings",
      superadminOnly: true,
      requiredPermission: "system.config.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.system" }),
    }),
  },
  {
    path: "/admin/system-config/ai",
    Component: AiConfigManagement,
    handle: routeHandle("ai-models", {
      labelKey: "nav.aiModels",
      icon: "brain",
      superadminOnly: true,
      requiredPermission: "ai.config.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.aiModels" }),
    }),
  },
  {
    path: "/account",
    Component: AccountPage,
    handle: {
      hideInNav: true,
      labelKey: "nav.account",
      icon: "user",
      breadcrumbs: crumbs({ labelKey: "nav.account" }),
    },
  },
  {
    path: "/settings",
    Component: SettingsPage,
    handle: {
      hideInNav: true,
      labelKey: "nav.settings",
      icon: "settings",
      breadcrumbs: crumbs({ labelKey: "nav.settings" }),
    },
  },
  {
    path: "/settings/system",
    Component: SettingsPage,
    handle: routeHandle("ai-models", {
      hideInNav: true,
      labelKey: "nav.system",
      icon: "settings",
      requiredPermission: "ai.config.view",
      breadcrumbs: crumbs(
        { labelKey: "nav.settings", to: "/settings" },
        { labelKey: "nav.system" },
      ),
    }),
  },
  {
    path: "/admin/cost",
    Component: CostCenterPage,
    handle: routeHandle("ai-costs", {
      labelKey: "nav.costCenter",
      icon: "gauge",
      superadminOnly: true,
      requiredPermission: "cost.center.read",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.costCenter" }),
    }),
  },
  {
    path: "/admin/notifications",
    Component: AdminNotificationsListPage,
    handle: routeHandle("system-notifications", {
      labelKey: "nav.admin.notifications",
      icon: "mail",
      superadminOnly: true,
      requiredPermission: "notification.admin.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.admin.notifications" }),
    }),
  },
  {
    path: "/admin/notifications/:id",
    Component: NotificationDetailPage,
    handle: routeHandle("system-notifications", {
      hideInNav: true,
      superadminOnly: true,
      requiredPermission: "notification.admin.view",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.admin.notifications", to: "/admin/notifications" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/admin/email-templates",
    Component: AdminEmailTemplatesPage,
    handle: routeHandle("email-templates", {
      labelKey: "nav.admin.emailTemplates",
      icon: "mail",
      superadminOnly: true,
      requiredPermission: "email.template.admin",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.admin.emailTemplates" }),
    }),
  },
  {
    path: "/admin/email-templates/:category",
    Component: AdminEmailTemplateDetailPage,
    handle: routeHandle("email-templates", {
      hideInNav: true,
      superadminOnly: true,
      requiredPermission: "email.template.admin",
      breadcrumbs: crumbs(
        ADMIN,
        {
          labelKey: "nav.admin.emailTemplates",
          to: "/admin/email-templates",
        },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    // Inbox in-app: disponible para todo perfil autenticado (la campana
    // del Topbar enlaza aquí). Sin permiso ni módulo gating (S48).
    path: "/notifications",
    Component: TenantNotificationsListPage,
    handle: {
      labelKey: "nav.notifications",
      icon: "mail",
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.notifications" }),
    },
  },
  {
    path: "/tenants/:tid/notifications",
    Component: TenantNotificationsListPage,
    handle: routeHandle("notifications", {
      hideInNav: true,
      requiredPermission: "notification.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.notifications" }),
    }),
  },
  {
    path: "/tenants/:tid/notifications/:id",
    Component: NotificationDetailPage,
    handle: routeHandle("notifications", {
      hideInNav: true,
      requiredPermission: "notification.view",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.notifications" },
        { labelKey: "breadcrumb.detail", usePageTitle: true },
      ),
    }),
  },
  {
    // Preferencias de notificación (campana, navegador, push, digest).
    // Sin permisos de correo: la config del motor vive en /admin/email-setting.
    path: "/settings/notifications",
    Component: TenantNotificationSettingsPage,
    handle: routeHandle("notifications", {
      hideInNav: true,
      requiredPermission: "isAuthenticated",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.notifications" },
        { labelKey: "nav.preferences" },
      ),
    }),
  },
  {
    path: "/admin/email-setting",
    Component: EmailConfigPage,
    handle: routeHandle("email-templates", {
      labelKey: "nav.correos",
      icon: "mail",
      requiredPermission: "email.config.manage",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.correos" },
        { labelKey: "nav.system" },
      ),
    }),
  },
  {
    path: "/admin/email-setting/plantillas",
    Component: EmailTemplatesPage,
    handle: routeHandle("email-templates", {
      hideInNav: true,
      requiredPermission: "email.template.view",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.correos", to: "/admin/email-setting" },
        { labelKey: "nav.templates" },
      ),
    }),
  },
  {
    path: "/admin/email-setting/plantillas/:category",
    Component: EmailTemplateEditorPage,
    handle: routeHandle("email-templates", {
      hideInNav: true,
      requiredPermission: "email.template.manage",
      breadcrumbs: crumbs(
        ADMIN,
        { labelKey: "nav.correos", to: "/admin/email-setting" },
        { labelKey: "nav.templates", to: "/admin/email-setting/plantillas" },
        { labelKey: "breadcrumb.edit", usePageTitle: true },
      ),
    }),
  },
  {
    path: "/settings/correos",
    Component: LegacyEmailSettingRedirect,
    handle: { hideInNav: true, requiredPermission: "isAuthenticated" },
  },
  {
    path: "/settings/correos/plantillas",
    Component: LegacyEmailTemplatesRedirect,
    handle: { hideInNav: true, requiredPermission: "isAuthenticated" },
  },
  {
    path: "/settings/correos/plantillas/:category",
    Component: LegacyEmailTemplatesRedirect,
    handle: { hideInNav: true, requiredPermission: "isAuthenticated" },
  },
  {
    // Redirects legado (S48): plantillas de correo migradas a /admin/email-setting.
    path: "/settings/notifications/templates",
    Component: LegacyEmailTemplatesRedirect,
    handle: { hideInNav: true, requiredPermission: "isAuthenticated" },
  },
  {
    path: "/settings/notifications/templates/:category",
    Component: LegacyEmailTemplatesRedirect,
    handle: { hideInNav: true, requiredPermission: "isAuthenticated" },
  },
  {
    path: "/admin/architecture",
    Component: ArchitecturePage,
    handle: routeHandle("architecture", {
      labelKey: "nav.architecture",
      icon: "server",
      superadminOnly: true,
      requiredPermission: "system.config.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.architecture" }),
    }),
  },
  {
    path: "/architecture",
    Component: ArchitecturePage,
    handle: routeHandle("architecture", {
      hideInNav: true,
      superadminOnly: true,
      requiredPermission: "system.config.view",
      breadcrumbs: crumbs(ADMIN, { labelKey: "nav.architecture" }),
    }),
  },
];

export const FALLBACK_ROUTE: RouteSpec = { path: "*", Component: NotFoundPage };
